import { Component, OnInit, ViewChild } from '@angular/core';

import { GptService } from './../../services/gpt/gpt.service';

declare const $: any;

import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { SurveyCreatorModel } from 'survey-creator-core';
import {
  CreateQuestionnaireTemplateDto,
  Questionnaire,
  QuestionnaireTemplate,
  SurveyJSON,
} from '../../services/gpt/types';
import { SelectedSubscriberService } from '../../services/selected-subscriber/selected-subscriber.service';
import { BaseComponent } from '../../modules/shared/base.component';
import { User } from '../../../types';
import { Model } from 'survey-core';

const creatorOptions = {
  showLogicTab: false,
  showPropertyGrid: false,
  isAutoSave: true,
  haveCommercialLicense: true,
  showToolbox: false,
};

type TCurrentMode = 'questionnaire' | 'template';

@Component({
  selector: 'app-questionnaire',
  templateUrl: './questionnaire.component.html',
  styleUrls: ['./questionnaire.component.css'],
})
export class QuestionnaireComponent extends BaseComponent implements OnInit {
  @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: false }) sort: MatSort;

  questionnaires: Questionnaire[] = [];
  templates: QuestionnaireTemplate[] = [];
  pageNo: number = 0;
  limit: number = 10;
  totalRecords: number;
  displayedColumns: string[] = [
    'title',
    'subscriberName',
    'createdAt',
    'answeredAt',
    'action',
  ];
  templateDisplayColumns: string[] = ['title', 'createdAt', 'action'];
  dataSource: MatTableDataSource<any>;
  currentSelected: Questionnaire | QuestionnaireTemplate;
  model: SurveyCreatorModel = new SurveyCreatorModel(creatorOptions);
  surveyModel: Model;
  currentMode: TCurrentMode = 'questionnaire';
  currentSort: { by: string; order?: 'ASC' | 'DESC' };
  subscriberId: number;

  error: string;
  isLoading: boolean;

  constructor(
    private gptService: GptService,
    private selectedSubscriberService: SelectedSubscriberService
  ) {
    super();
  }

  ngOnInit(): void {
    this.subscriberId = this.selectedSubscriberService.user?.id;

    this.currentSort = {
      by: 'createdAt',
      order: 'DESC',
    };

    this.getData();
  }

  getTemplates() {
    this.isLoading = true;
    this.subscriptions.add(
      this.gptService
        .getTemplates({
          pageOffset: this.pageNo,
          pageSize: this.limit,
          sortField: this.currentSort.by,
          sortOrder: this.currentSort.order,
        })
        .subscribe(
          (data) => {
            this.totalRecords = data.totalCount;
            this.templates = data.results || [];

            this.isLoading = false;
          },
          (error) => {
            this.error = error.message;
            this.isLoading = false;
          }
        )
    );
  }

  getData() {
    this.currentSelected = null;
    if (this.currentMode == 'template') {
      this.getTemplates();
    } else {
      this.getQuestionnaires();
    }
  }

  getQuestionnaires() {
    this.isLoading = true;
    this.error = '';
    this.subscriptions.add(
      this.gptService
        .getQuestionnaires({
          pageOffset: this.pageNo,
          pageSize: this.limit,
          sortField: this.currentSort.by,
          sortOrder: this.currentSort.order,
        })
        .subscribe(
          (data) => {
            this.totalRecords = data.totalCount;
            this.questionnaires = data.results || [];
            this.isLoading = false;
          },
          (error) => {
            this.error = error.message;
            this.isLoading = false;
          }
        )
    );
  }

  goToPage(newPageNumber: number) {
    this.pageNo = newPageNumber;
    this.getData();
  }

  viewEditorForm(data: Questionnaire | QuestionnaireTemplate) {
    this.currentSelected = data;
    this.createQuestionnaire(this.currentSelected.questionnaireSchema);
    $('#open-edit-question').modal('show');
  }

  viewForm(data: Questionnaire | QuestionnaireTemplate) {
    this.currentSelected = data;
    const survey = new Model(this.currentSelected.questionnaireSchema);

    survey.mode = 'display';
    this.surveyModel = survey;
    $('#open-question').modal('show');
  }

  viewAnswerForm(data: Questionnaire) {
    const survey = new Model(data.questionnaireSchema);
    survey.data = data.response?.answers.answers.reduce(
      (prev, next) => ({ ...prev, [next.name]: next.value }),
      {}
    );
    survey.mode = 'display';
    this.surveyModel = survey;
    $('#open-question').modal('show');
  }

  closeModal() {
    $('#open-edit-question').modal('hide');
    $('#open-question').modal('hide');
  }

  createQuestionnaire(schema: SurveyJSON) {
    const creator = new SurveyCreatorModel(creatorOptions);
    creator.haveCommercialLicense = true;

    creator.text = JSON.stringify(schema);
    creator.toolbox.removeItem('descriptivetext');
    creator.readOnly = this.currentMode === 'questionnaire';
    this.model = creator;
  }

  getFullName(user?: User): string {
    if (user?.userProfile) {
      return `${user.userProfile.firstName} ${user.userProfile.lastName}`;
    }
    return '';
  }

  sortData(event: any) {
    let order: 'ASC' | 'DESC';

    switch (event.direction) {
      case 'asc': {
        order = 'ASC';
        break;
      }
      case 'desc': {
        order = 'DESC';
        break;
      }
      default:
        break;
    }

    this.pageNo = 0;
    this.currentSort = { by: order ? event.active : '', order };

    this.getData();
  }

  goToPreviousPage() {
    this.pageNo--;
    this.getData();
  }

  goToNextPage() {
    this.pageNo++;
    this.getData();
  }

  getPageNumbers(): number[] {
    const totalPages = this.getTotalPages();
    return Array(totalPages)
      .fill(0)
      .map((_, i) => i);
  }

  getTotalPages(): number {
    return this.totalRecords ? Math.ceil(this.totalRecords / this.limit) : 0;
  }

  updateTemplate() {
    const data: CreateQuestionnaireTemplateDto = {
      questionnaireSchema: JSON.parse(this.model.text),
    };
    this.isLoading = true;
    this.subscriptions.add(
      this.gptService.updateTemplate(this.currentSelected.id, data).subscribe(
        () => {
          this.closeModal();
          this.getData();
        },
        (error) => {
          this.error = error;
          this.isLoading = false;
        }
      )
    );
  }

  deleteTemplate(data: QuestionnaireTemplate) {
    this.isLoading = true;
    this.subscriptions.add(
      this.gptService.deleteTemplate(data.id).subscribe(
        () => {
          this.getData();
        },
        (error) => {
          this.error = error;
          this.isLoading = false;
        }
      )
    );
  }

  changeMode(mode: TCurrentMode) {
    this.currentMode = mode;
    this.pageNo = 0;
    this.totalRecords = 0;
    this.currentSort = {
      by: 'createdAt',
      order: 'DESC',
    };

    this.getData();
  }
}
